import React, { useEffect, useContext, useState } from "react";
import ReactDOM from "react-dom";
import Button from "ui/Button";
import ModalContext from "./Context";

interface ModalProps {
    show: boolean;
    title?: string;
    onClose: (e?: React.SyntheticEvent) => void;
    onTopClose?: (e?: React.SyntheticEvent) => void;
    onConfirm: () => void;
    cancelButton?: string;
    cancelClassName?: string;
    confirmButton?: string;
    message: React.ReactNode;
    errorCount?: string;
    subTitle?: string;
    dontDisplayIconClose?: boolean;
    zIndexIncrement?: number;
    infoMessage?: string;
}

const ConfirmModal: React.FC<ModalProps> = ({
    show,
    title,
    onClose,
    onTopClose,
    onConfirm,
    cancelButton,
    cancelClassName,
    confirmButton,
    message,
    errorCount,
    subTitle,
    dontDisplayIconClose,
    zIndexIncrement = 0,
    infoMessage,
}) => {
    const modal = useContext(ModalContext);
    const [element, setPortalElement] = useState<HTMLElement | undefined>(undefined);

    useEffect(() => {
        if (modal.isMain) {
            if (show) {
                document.documentElement.classList.add("overflow-hidden");
            } else {
                document.documentElement.classList.remove("overflow-hidden");
            }
        }
    }, [show, modal.isMain]);

    useEffect(() => {
        const newElement = document.createElement("div") as HTMLElement;
        document.body.appendChild(newElement);
        setPortalElement(newElement);
        return () => {
            if (modal.isMain) {
                document.documentElement.classList.remove("overflow-hidden");
            }
            document.body.removeChild(newElement);
        };
    }, [modal.isMain]);

    const zIndex = modal.overlay.zIndex + zIndexIncrement;

    if (show && element) {
        return ReactDOM.createPortal(
            <div className="fixed inset-0 flex items-center justify-center px-4" style={{ zIndex }}>
                {/* Backdrop */}
                <div
                    className="absolute inset-0 bg-black/50"
                    onClick={onTopClose || onClose}
                />
                {/* Modal container */}
                <div className="relative w-full max-w-md bg-white rounded-xl shadow-xl p-6">
                    {!dontDisplayIconClose && (
                        <button
                            type="button"
                            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-muted-foreground hover:bg-secondary transition-colors"
                            onClick={onTopClose || onClose}
                        >
                            <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                            </svg>
                        </button>
                    )}

                    {title && (
                        <h2 className="text-lg font-semibold text-foreground text-center mb-2 pr-8">{title}</h2>
                    )}
                    {subTitle && (
                        <p className="text-sm text-muted-foreground text-center mb-3">{subTitle}</p>
                    )}

                    <ModalContext.Provider
                        value={{
                            isOpened: true,
                            overlay: { zIndex: zIndex + 50 },
                            isMain: false,
                        }}
                    >
                        {errorCount && (
                            <p className="text-sm text-destructive mb-3">{errorCount}</p>
                        )}
                        <p className="text-sm text-muted-foreground text-center mb-6">{message}</p>

                        <div className="flex gap-3 justify-center">
                            {cancelButton && (
                                <Button
                                    className="flex-1 px-4 py-2 text-sm font-medium border border-border rounded-lg text-foreground hover:bg-secondary transition-colors"
                                    onClick={onClose}
                                >
                                    {cancelButton}
                                </Button>
                            )}
                            {confirmButton && (
                                <Button
                                    className={cancelClassName ?? "flex-1 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"}
                                    onClick={onConfirm}
                                >
                                    {confirmButton}
                                </Button>
                            )}
                        </div>
                    </ModalContext.Provider>

                    {infoMessage && (
                        <p className="text-xs text-muted-foreground text-center mt-4">{infoMessage}</p>
                    )}
                </div>
            </div>,
            element
        );
    }
    return null;
};

export default ConfirmModal;
